import React from "react";

import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useFeaturesState } from "contexts/features";
import { useUserState } from "contexts/user";
import { hasARoleWithPermission } from "utils/roleBasedAccess";

import NoPermissionPage from "./NoPermissionPage";
import PageNotFound from "./PageNotFound";

const AppRouteGenerator = ({ CLIENT_ROUTES, features, ...otherProps }) => {
  const user = useUserState();
  const { pathname: path } = useLocation();
  const { permissible: permissibleFeatures } = useFeaturesState();

  const route = links =>
    links.reduce(
      (acc, link) =>
        link?.items ? [...acc, route(link.items)] : [...acc, link.to],
      []
    )[0];

  const getHomeRoute = () => {
    if (path.split("/")[1] === "management") {
      return route(permissibleFeatures.management);
    } else if (path.split("/")[1] === "consumption") {
      return route(permissibleFeatures.consumption);
    } else if (path.split("/")[1] === "ingestion") {
      return route(permissibleFeatures.ingestion);
    }

    return null;
  };

  const hasAnAppOrSubAppWithPermissions = (feature, app = features) => {
    const featureRoles = app?.[feature]?.roles;
    return featureRoles
      ? hasARoleWithPermission(user?.role, featureRoles)
      : Object.keys(app?.[feature])
          .map(subApp =>
            hasAnAppOrSubAppWithPermissions(subApp, app?.[feature])
          )
          ?.includes(true);
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Navigate
            to={CLIENT_ROUTES[
              Object.keys(features)?.filter(feature =>
                hasAnAppOrSubAppWithPermissions(feature) ? feature : ""
              )?.[0]
            ]?.path?.replace("*", "")}
          />
        }
      />
      {Object.keys(features).map(feature => {
        if (hasAnAppOrSubAppWithPermissions(feature)) {
          const Component = CLIENT_ROUTES?.[feature]?.component;
          return (
            <Route
              key={CLIENT_ROUTES?.[feature]?.path}
              path={CLIENT_ROUTES?.[feature]?.path}
              element={
                <Component features={features?.[feature]} {...otherProps} />
              }
            />
          );
        }

        return (
          <Route
            key={CLIENT_ROUTES?.[feature]?.path}
            path={CLIENT_ROUTES?.[feature]?.path}
            element={<NoPermissionPage homeRoute={getHomeRoute()} />}
          />
        );
      })}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default AppRouteGenerator;
